import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { publicApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Loader2, AlertCircle } from 'lucide-react';

export default function TenantLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [tenantSlug, setTenantSlug] = useState(localStorage.getItem('tenant_slug') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [step, setStep] = useState(tenantSlug ? 2 : 1);

  const handleTenantCheck = async () => {
    if (!tenantSlug.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await publicApi.getTenantInfo(tenantSlug.trim());
      setTenantInfo(res.data);
      localStorage.setItem('tenant_slug', tenantSlug.trim());
      setStep(2);
    } catch {
      setError('Ecole non trouvee. Verifiez l\'identifiant.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/app/dashboard');
    } catch (err) {
      setError(err.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = tenantInfo?.settings?.primary_color || '#16a34a';

  return (
    <div className="min-h-screen bg-[#fafaf8] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: primaryColor }}>
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-xl font-bold text-stone-900">
            {tenantInfo ? tenantInfo.name : 'Connexion'}
          </h1>
          {!tenantInfo && <p className="text-sm text-stone-500 mt-1">Accedez a votre espace ecole</p>}
        </div>

        <Card className="border-stone-200">
          <CardContent className="p-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-lg mb-4" data-testid="login-error">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>Identifiant de l'ecole</Label>
                  <Input
                    value={tenantSlug}
                    onChange={(e) => setTenantSlug(e.target.value)}
                    placeholder="Ex: al-azhar"
                    data-testid="tenant-slug-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleTenantCheck()}
                  />
                  <p className="text-xs text-stone-400 mt-1">L'identifiant fourni lors de l'inscription</p>
                </div>
                <Button onClick={handleTenantCheck} disabled={loading || !tenantSlug.trim()} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="check-tenant-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continuer'}
                </Button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleLogin} className="space-y-4">
                {tenantInfo && (
                  <button
                    type="button"
                    onClick={() => { setStep(1); setTenantInfo(null); setError(''); localStorage.removeItem('tenant_slug'); }}
                    className="text-xs text-stone-500 hover:text-stone-700 mb-2 block"
                    data-testid="change-tenant-btn"
                  >
                    Changer d'ecole
                  </button>
                )}
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
                </div>
                <div>
                  <Label>Mot de passe</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full" style={{ backgroundColor: primaryColor }} data-testid="login-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Se connecter'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <button onClick={() => navigate('/')} className="text-sm text-stone-400 hover:text-stone-600" data-testid="back-landing-btn">
            Retour a l'accueil
          </button>
        </div>
      </div>
    </div>
  );
}
